import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowUpRight,
  Calendar,
  Camera,
  Car,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Cog,
  Copy,
  Flame,
  Fuel,
  Gauge,
  Hash,
  KeyRound,
  Layers,
  Leaf,
  Lightbulb,
  Mail,
  MapPin,
  Navigation,
  Phone,
  Settings2,
  Share2,
  ShieldCheck,
  Snowflake,
  Speaker,
  Sun,
  Tag,
  Truck,
  Palette,
  Clock,
  CreditCard,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VehicleGalleryPreviewStrip from "@/components/VehicleGalleryPreviewStrip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import { vehicleGallery, vehicleVideos } from "@/utils/vehicleImages";
import { formatVehiclePrice, isPriceNotDecided } from "@/utils/vehiclePrice";
import {
  formatMileage,
  localizedVehicleList,
  localizedVehicleText,
  vehicleHeadline,
} from "@/utils/vehicleDisplay";
import { setDocumentTitle } from "@/utils/pageTitle";
import { trackLead, trackPageView } from "@/utils/ga4";
import BotProtection from "@/components/BotProtection";
import ConsentAgreement from "@/components/ConsentAgreement";
import PhoneConsentSlide from "@/components/PhoneConsentSlide";
import { formatApiError } from "@/utils/apiError";
import { isFilled, isEmailFilled } from "@/utils/formReady";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEALER_PHONE = "(708) 222-6100";
const DEALER_TEL = "tel:+17082226100";

const conditionValueMap = {
  new: "vehicleModal.values.conditionNew",
  used: "vehicleModal.values.conditionUsed",
  "certified pre owned": "vehicleModal.values.conditionCertified",
  "certified pre-owned": "vehicleModal.values.conditionCertified",
  "certified preowned": "vehicleModal.values.conditionCertified",
  cpo: "vehicleModal.values.conditionCertified",
};

const fuelValueMap = {
  gasoline: "vehicleModal.values.fuelGasoline",
  gas: "vehicleModal.values.fuelGasoline",
  petrol: "vehicleModal.values.fuelGasoline",
  electric: "vehicleModal.values.fuelElectric",
  diesel: "vehicleModal.values.fuelDiesel",
  hybrid: "vehicleModal.values.fuelHybrid",
};

const transmissionValueMap = {
  automatic: "vehicleModal.values.transmissionAutomatic",
  auto: "vehicleModal.values.transmissionAutomatic",
};

function translateVehicleValue(raw, map, t) {
  if (raw == null || raw === "") return raw;
  const key = String(raw).trim().toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ");
  const tKey = map[key];
  if (!tKey) return raw;
  const translated = t(tKey);
  return translated.startsWith("vehicleModal.") ? raw : translated;
}

function featureIcon(label) {
  const s = String(label || "").toLowerCase();
  if (s.includes("camera") || s.includes("backup")) return Camera;
  if (s.includes("leather") || s.includes("seat")) return Car;
  if (s.includes("sun") || s.includes("moonroof") || s.includes("roof")) return Sun;
  if (s.includes("nav")) return Navigation;
  if (s.includes("bluetooth") || s.includes("audio") || s.includes("radio") || s.includes("speaker")) return Speaker;
  if (s.includes("heat")) return Flame;
  if (s.includes("cool") || s.includes("climate") || s.includes("air condition") || s.includes("a/c")) return Snowflake;
  if (s.includes("keyless") || s.includes("remote") || s.includes("smart key")) return KeyRound;
  if (s.includes("airbag") || s.includes("safety") || s.includes("brake") || s.includes("abs")) return ShieldCheck;
  if (s.includes("led") || s.includes("light") || s.includes("lamp") || s.includes("headlight")) return Lightbulb;
  if (s.includes("tow") || s.includes("hitch")) return Truck;
  if (s.includes("alloy") || s.includes("wheel")) return CircleDot;
  if (s.includes("cruise")) return Gauge;
  return Check;
}

export default function VehicleDetailPage() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { isDark } = useTheme();

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [mediaMode, setMediaMode] = useState("image");
  const [inquiryForm, setInquiryForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    preferred_contact: "email",
  });
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [consent, setConsent] = useState(false);
  const phoneProvided = isFilled(inquiryForm.phone);
  const canSubmit =
    isFilled(inquiryForm.name) &&
    isEmailFilled(inquiryForm.email) &&
    (!phoneProvided || consent);

  const loadVehicle = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const { data } = await axios.get(`${API}/vehicles/${vehicleId}`);
      setVehicle(data);
      setActiveImageIndex(0);
      setActiveVideoIndex(0);
      const gallery = vehicleGallery(data);
      const videos = vehicleVideos(data);
      setMediaMode(gallery.length ? "image" : videos.length ? "video" : "image");
    } catch (error) {
      setVehicle(null);
      setNotFound(error.response?.status === 404);
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    loadVehicle();
  }, [loadVehicle]);

  useEffect(() => {
    if (!vehicle) {
      setDocumentTitle(`/inventory/${vehicleId || ""}`);
    } else {
      document.title = `Drive Now Auto Haus - ${vehicleHeadline(vehicle)}`;
    }
    if (loading) return;
    trackPageView(
      `/inventory/${vehicleId || ""}`,
      vehicle
        ? {
            content_group: "vehicle",
            item_id: vehicle.id,
            item_name: vehicleHeadline(vehicle),
          }
        : { content_group: "vehicle" }
    );
  }, [vehicle, vehicleId, language, loading]);

  const gallery = useMemo(() => vehicleGallery(vehicle), [vehicle]);
  const videos = useMemo(() => vehicleVideos(vehicle), [vehicle]);
  const displayEngine = localizedVehicleText(language, vehicle?.engine, vehicle?.engine_es);
  const displayExterior = localizedVehicleText(language, vehicle?.exterior_color, vehicle?.exterior_color_es);
  const displayInterior = localizedVehicleText(language, vehicle?.interior_color, vehicle?.interior_color_es);
  const displayBodyType = localizedVehicleText(language, vehicle?.body_type, vehicle?.body_type_es);
  const displayFeatures = localizedVehicleList(language, vehicle?.features, vehicle?.features_es);
  const descriptionPoints = localizedVehicleList(
    language,
    vehicle?.description_points,
    vehicle?.description_points_es
  );

  const specs = vehicle
    ? [
        { icon: Calendar, label: t("vehicleModal.year"), value: vehicle.year },
        { icon: Gauge, label: t("vehicleModal.mileage"), value: formatMileage(vehicle.mileage) },
        { icon: Fuel, label: t("vehicleModal.fuelType"), value: translateVehicleValue(vehicle.fuel_type, fuelValueMap, t) },
        { icon: Cog, label: t("vehicleModal.transmission"), value: translateVehicleValue(vehicle.transmission, transmissionValueMap, t) },
        { icon: Car, label: t("vehicleModal.engine"), value: displayEngine },
        { icon: Palette, label: t("vehicleModal.exteriorColor"), value: displayExterior },
        { icon: Palette, label: t("vehicleModal.interiorColor"), value: displayInterior },
        { icon: Hash, label: t("vehiclePage.vin"), value: vehicle.vin },
        { icon: Tag, label: t("vehicleModal.trim"), value: vehicle.trim },
        { icon: Layers, label: t("vehicleModal.bodyType"), value: displayBodyType },
        { icon: Leaf, label: t("vehicleModal.condition"), value: translateVehicleValue(vehicle.condition, conditionValueMap, t) },
        { icon: Settings2, label: t("vehicleModal.drivetrain"), value: vehicle.drivetrain },
        { icon: CircleDot, label: t("vehicleModal.cylinders"), value: vehicle.cylinders },
        { icon: Fuel, label: t("vehicleModal.cityMpg"), value: vehicle.city_mpg != null && vehicle.city_mpg !== "" ? vehicle.city_mpg : null },
        { icon: Fuel, label: t("vehicleModal.highwayMpg"), value: vehicle.hwy_mpg != null && vehicle.hwy_mpg !== "" ? vehicle.hwy_mpg : null },
        { icon: Tag, label: t("vehiclePage.stock"), value: vehicle.stockNumber },
      ].filter((s) => s.value !== null && s.value !== undefined && String(s.value).trim() !== "")
    : [];

  const highlightSpecs = specs.slice(0, 8);
  const priceLabel = formatVehiclePrice(vehicle);

  const pageBg = isDark ? "bg-[#050505]" : "bg-[#FAFAFA]";
  const cardCls = isDark ? "bg-[#0A0A0A] border-white/10" : "bg-white border-gray-200";
  const muted = isDark ? "text-gray-400" : "text-gray-600";
  const heading = isDark ? "text-white" : "text-gray-900";
  const accent = isDark ? "text-blue-500" : "text-red-600";
  const accentBg = isDark ? "bg-blue-600 hover:bg-blue-700" : "bg-red-600 hover:bg-red-700";
  const outlineBtn = isDark
    ? "border-white/20 text-white hover:bg-white/10 hover:text-white"
    : "border-gray-300 text-gray-900 hover:bg-gray-100 hover:text-gray-900";
  const inputCls = isDark
    ? "bg-black/50 border-white/10 text-white h-12 rounded-none"
    : "bg-white border-gray-300 text-gray-900 h-12 rounded-none";

  const updateInquiryPhone = (value) => {
    setInquiryForm((f) => ({
      ...f,
      phone: value,
      preferred_contact: !isFilled(value) && f.preferred_contact === "phone" ? "email" : f.preferred_contact,
    }));
    if (!isFilled(value)) setConsent(false);
  };

  const handleSubmitInquiry = async (e) => {
    e.preventDefault();
    if (!vehicle?.id) return;
    const phone = inquiryForm.phone.trim();
    if (phone && !consent) {
      toast.error(t("consent.formRequired"));
      return;
    }
    const preferredContact = phone && inquiryForm.preferred_contact === "phone"
      ? "phone"
      : inquiryForm.preferred_contact === "phone"
        ? "email"
        : inquiryForm.preferred_contact;
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/inquiry`, {
        vehicle_id: vehicle.id,
        ...inquiryForm,
        phone,
        preferred_contact: preferredContact,
        consent: Boolean(phone && consent),
        captcha_token: captchaToken,
      });
      toast.success(response.data.message);
      trackLead("vehicle_inquiry", { item_id: vehicle.id });
      setInquiryForm({ name: "", email: "", phone: "", message: "", preferred_contact: "email" });
      setConsent(false);
    } catch (error) {
      toast.error(formatApiError(error, "Failed to submit inquiry. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("vehiclePage.linkCopied"));
    } catch {
      toast.error(t("vehiclePage.copyLink"));
    }
  };

  const sharePage = async () => {
    const url = window.location.href;
    const title = vehicleHeadline(vehicle);
    if (navigator.share) {
      try {
        await navigator.share({ title, url, text: title });
        return;
      } catch {
        /* user cancelled or share failed — fall through to copy */
      }
    }
    copyLink();
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${pageBg}`} data-testid="vehicle-detail-page">
      <Navbar />

      <div className="pt-28 md:pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading && (
            <div className={`${muted} font-['Manrope']`} data-testid="vehicle-detail-loading">
              {t("vehiclePage.loading")}
            </div>
          )}

          {!loading && (notFound || !vehicle) && (
            <div className={`max-w-xl border p-10 ${cardCls}`}>
              <h1 className={`font-unbounded text-3xl font-bold mb-3 ${heading}`}>{t("vehiclePage.notFound")}</h1>
              <p className={`font-['Manrope'] mb-8 ${muted}`}>{t("vehiclePage.notFoundHint")}</p>
              <Button
                onClick={() => navigate("/inventory")}
                className={`rounded-none text-white uppercase tracking-wider ${accentBg}`}
              >
                {t("vehiclePage.browseInventory")}
              </Button>
            </div>
          )}

          {!loading && vehicle && (
            <>
              <div className={`flex flex-wrap items-center gap-2 text-sm font-['Manrope'] mb-8 ${muted}`}>
                <Link to="/inventory" className={isDark ? "hover:text-blue-400" : "hover:text-red-600"}>{t("vehiclePage.breadcrumbInventory")}</Link>
                <ChevronRight className="h-4 w-4" />
                <span className={heading}>{vehicleHeadline(vehicle)}</span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="lg:col-span-3 min-w-0"
                >
                  <div className="relative w-full aspect-[16/10] bg-black overflow-hidden">
                    {mediaMode === "video" && videos.length > 0 ? (
                      <video
                        key={videos[Math.min(activeVideoIndex, videos.length - 1)]?.url}
                        src={videos[Math.min(activeVideoIndex, videos.length - 1)]?.url}
                        className="w-full h-full object-contain bg-black"
                        controls
                        playsInline
                        preload="metadata"
                      />
                    ) : gallery.length > 0 ? (
                      <img
                        src={gallery[Math.min(activeImageIndex, gallery.length - 1)]}
                        alt={vehicleHeadline(vehicle)}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center ${muted}`}>
                        {t("vehiclePage.noPhotos")}
                      </div>
                    )}

                    {vehicle.featured && (
                      <div className="absolute top-4 left-4 z-10">
                        <span className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white ${isDark ? "bg-blue-600" : "bg-red-600"}`}>
                          {t("featured.badge")}
                        </span>
                      </div>
                    )}

                    {videos.length > 0 && (
                      <div className="absolute top-4 right-4 z-10 flex overflow-hidden border border-white/20">
                        {mediaMode === "video" && gallery.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setMediaMode("image")}
                            className="px-3 py-2 text-xs font-semibold uppercase tracking-wider bg-black/60 text-white hover:bg-black/80"
                          >
                            {t("vehiclePage.photos")}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setMediaMode(mediaMode === "video" ? "image" : "video")}
                          className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider ${
                            mediaMode === "video" ? "bg-white text-black" : "bg-black/60 text-white hover:bg-black/80"
                          }`}
                        >
                          {t("vehiclePage.videos")} ({videos.length})
                        </button>
                      </div>
                    )}

                    {mediaMode === "image" && gallery.length > 1 && (
                      <>
                        <button
                          type="button"
                          aria-label="Previous image"
                          onClick={() => setActiveImageIndex((i) => (i - 1 + gallery.length) % gallery.length)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/60 text-white hover:bg-black/80"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          aria-label="Next image"
                          onClick={() => setActiveImageIndex((i) => (i + 1) % gallery.length)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2 bg-black/60 text-white hover:bg-black/80"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                        <div className="absolute bottom-3 right-3 z-10 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-black/70 text-white">
                          {activeImageIndex + 1} {t("vehiclePage.photoOf")} {gallery.length}
                        </div>
                      </>
                    )}
                  </div>

                  {mediaMode === "image" && gallery.length > 1 && (
                    <VehicleGalleryPreviewStrip
                      gallery={gallery}
                      activeImageIndex={activeImageIndex}
                      onSelect={setActiveImageIndex}
                      isDark={isDark}
                      photoLabel={t("vehiclePage.photos")}
                    />
                  )}

                  {mediaMode === "video" && videos.length > 1 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {videos.map((v, idx) => (
                        <button
                          key={`${v.url}-${idx}`}
                          type="button"
                          onClick={() => setActiveVideoIndex(idx)}
                          className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider border ${
                            idx === activeVideoIndex
                              ? isDark
                                ? "bg-blue-600 border-blue-500 text-white"
                                : "bg-red-600 border-red-600 text-white"
                              : isDark
                                ? "border-white/20 text-white hover:bg-white/10"
                                : "border-gray-300 text-gray-900 hover:bg-gray-50 hover:text-gray-900"
                          }`}
                        >
                          {t("vehiclePage.videos")} {idx + 1}
                        </button>
                      ))}
                    </div>
                  )}
                </motion.div>

                <motion.aside
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                  className="lg:col-span-2 lg:sticky lg:top-28 h-fit"
                >
                  <span className={`text-sm uppercase tracking-widest font-medium ${accent}`}>{vehicle.make}</span>
                  <h1 className={`font-unbounded text-3xl md:text-4xl font-bold mt-2 ${heading}`}>
                    {vehicle.year} {vehicle.model}{vehicle.trim ? ` ${vehicle.trim}` : ""}
                  </h1>

                  <div className="mt-5">
                    {priceLabel ? (
                      <>
                        <p className={`font-unbounded text-4xl font-bold ${heading}`}>{priceLabel}</p>
                        <p className={`text-sm mt-1 ${muted}`}>{t("vehiclePage.plusFees")}</p>
                      </>
                    ) : (
                      <>
                        <p className={`font-unbounded text-2xl font-bold ${heading}`}>{t("vehicleModal.getQuote")}</p>
                        <p className={`text-sm mt-1 ${muted}`}>{t("vehicleModal.quotePrompt")}</p>
                      </>
                    )}
                  </div>

                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {highlightSpecs.slice(0, 6).map((spec) => (
                      <div key={spec.label} className={`p-3 border ${cardCls}`}>
                        <div className={`flex items-center gap-2 mb-1 ${muted}`}>
                          <spec.icon className="h-4 w-4" />
                          <span className="text-[10px] uppercase tracking-wider">{spec.label}</span>
                        </div>
                        <p className={`font-medium text-sm truncate ${heading}`}>{spec.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex flex-col gap-3">
                    <a href="#vehicle-inquiry">
                      <Button className={`w-full rounded-none py-6 text-white uppercase tracking-wider ${accentBg}`}>
                        <Mail className="h-4 w-4 mr-2" />
                        {isPriceNotDecided(vehicle) ? t("vehicleModal.getQuote") : t("vehicleModal.interestedTitle")}
                      </Button>
                    </a>
                    <a href={DEALER_TEL}>
                      <Button variant="outline" className={`w-full rounded-none py-6 uppercase tracking-wider ${outlineBtn}`}>
                        <Phone className="h-4 w-4 mr-2" />
                        {t("vehiclePage.callDealership")} {DEALER_PHONE}
                      </Button>
                    </a>
                    <Link to="/financing">
                      <Button variant="outline" className={`w-full rounded-none py-6 uppercase tracking-wider ${outlineBtn}`}>
                        <CreditCard className="h-4 w-4 mr-2" />
                        {t("vehiclePage.applyFinancing")}
                      </Button>
                    </Link>
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" onClick={copyLink} className={`flex-1 rounded-none ${outlineBtn}`}>
                        <Copy className="h-4 w-4 mr-2" />
                        {t("vehiclePage.copyLink")}
                      </Button>
                      <Button type="button" variant="outline" onClick={sharePage} className={`flex-1 rounded-none ${outlineBtn}`}>
                        <Share2 className="h-4 w-4 mr-2" />
                        {t("vehiclePage.share")}
                      </Button>
                    </div>
                  </div>

                  <div className={`mt-8 p-4 border ${cardCls}`}>
                    <p className={`text-xs uppercase tracking-widest mb-3 ${accent}`}>{t("vehiclePage.locationLabel")}</p>
                    <div className={`flex items-start gap-3 text-sm font-['Manrope'] ${muted}`}>
                      <MapPin className={`h-4 w-4 mt-0.5 ${accent}`} />
                      <span>{t("vehiclePage.address")}</span>
                    </div>
                    <div className={`flex items-start gap-3 text-sm font-['Manrope'] mt-3 ${muted}`}>
                      <Clock className={`h-4 w-4 mt-0.5 ${accent}`} />
                      <span>{t("vehiclePage.hours")}</span>
                    </div>
                  </div>
                </motion.aside>
              </div>

              {descriptionPoints.length > 0 && (
                <section className="mt-16">
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-10 h-10 flex items-center justify-center ${isDark ? "bg-blue-600/10" : "bg-red-600/10"}`}>
                      <ArrowUpRight className={`h-5 w-5 ${accent}`} />
                    </div>
                    <h2 className={`font-unbounded text-2xl font-semibold ${heading}`}>{t("vehiclePage.highlights")}</h2>
                  </div>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {descriptionPoints.map((point, index) => (
                      <li key={`${index}-${point.slice(0, 24)}`} className={`flex items-start gap-3 p-5 border ${cardCls}`}>
                        <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${isDark ? "bg-blue-500" : "bg-red-600"}`} />
                        <span className={`text-sm leading-relaxed font-['Manrope'] ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="mt-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-10 h-10 flex items-center justify-center ${isDark ? "bg-blue-600/10" : "bg-red-600/10"}`}>
                    <Settings2 className={`h-5 w-5 ${accent}`} />
                  </div>
                  <h2 className={`font-unbounded text-2xl font-semibold ${heading}`}>{t("vehiclePage.specifications")}</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {specs.map((spec) => (
                    <div key={spec.label} className={`p-4 border ${cardCls}`}>
                      <div className={`flex items-center gap-2 mb-2 ${muted}`}>
                        <spec.icon className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-wider">{spec.label}</span>
                      </div>
                      <p className={`font-medium text-sm ${heading}`}>{spec.value}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mt-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-10 h-10 flex items-center justify-center ${isDark ? "bg-blue-600/10" : "bg-red-600/10"}`}>
                    <ShieldCheck className={`h-5 w-5 ${accent}`} />
                  </div>
                  <h2 className={`font-unbounded text-2xl font-semibold ${heading}`}>{t("vehiclePage.features")}</h2>
                </div>
                {displayFeatures.length ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {displayFeatures.map((feature, index) => {
                      const Icon = featureIcon(feature);
                      return (
                        <div key={`${index}-${feature}`} className={`flex items-center gap-3 p-4 border ${cardCls}`}>
                          <Icon className={`h-4 w-4 shrink-0 ${accent}`} />
                          <span className={`text-sm font-['Manrope'] ${isDark ? "text-gray-200" : "text-gray-800"}`}>{feature}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className={`font-['Manrope'] ${muted}`}>{t("vehiclePage.noFeatures")}</p>
                )}
              </section>

              <section id="vehicle-inquiry" className="mt-16 scroll-mt-28">
                <div className={`border p-6 md:p-10 ${cardCls}`}>
                  <h2 className={`font-unbounded text-2xl font-semibold mb-2 ${heading}`}>{t("vehiclePage.inquireHeading")}</h2>
                  <p className={`font-['Manrope'] mb-8 ${muted}`}>{t("vehiclePage.inquireHint")}</p>
                  <form onSubmit={handleSubmitInquiry} className="space-y-4" data-testid="vehicle-page-inquiry-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className={muted}>{t("contact.nameLabel")} *</Label>
                        <Input
                          value={inquiryForm.name}
                          onChange={(e) => setInquiryForm((f) => ({ ...f, name: e.target.value }))}
                          required
                          placeholder={t("vehicleModal.namePlaceholder")}
                          className={inputCls}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className={muted}>{t("contact.emailLabel")} *</Label>
                        <Input
                          type="email"
                          value={inquiryForm.email}
                          onChange={(e) => setInquiryForm((f) => ({ ...f, email: e.target.value }))}
                          required
                          placeholder={t("vehicleModal.emailPlaceholder")}
                          className={inputCls}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className={muted}>{t("contact.phoneLabel")} ({t("common.optional")})</Label>
                      <Input
                        type="tel"
                        value={inquiryForm.phone}
                        onChange={(e) => updateInquiryPhone(e.target.value)}
                        placeholder={t("vehicleModal.phonePlaceholder")}
                        className={inputCls}
                        data-testid="vehicle-page-inquiry-phone-input"
                      />
                    </div>
                    <PhoneConsentSlide open={phoneProvided}>
                      <ConsentAgreement
                        id="vehicle-page-inquiry-consent"
                        checked={consent}
                        onCheckedChange={setConsent}
                        isDark={isDark}
                        testId="vehicle-page-inquiry-consent-checkbox"
                        checkboxClassName={`mt-1 ${isDark ? "" : "border-gray-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"}`}
                      />
                    </PhoneConsentSlide>
                    <div className="space-y-2">
                      <Label className={muted}>{t("contact.messageLabel")} ({t("common.optional")})</Label>
                      <Textarea
                        value={inquiryForm.message}
                        onChange={(e) => setInquiryForm((f) => ({ ...f, message: e.target.value }))}
                        placeholder={t("vehicleModal.messagePlaceholder")}
                        className={`rounded-none min-h-[100px] resize-none ${
                          isDark ? "bg-black/50 border-white/10 text-white" : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className={muted}>{t("vehicleModal.preferredContact")}</Label>
                      <RadioGroup
                        value={inquiryForm.preferred_contact}
                        onValueChange={(value) => setInquiryForm((f) => ({ ...f, preferred_contact: value }))}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="email" id="page-contact-email" className={isDark ? "border-white/20 text-blue-500" : "border-gray-400 text-red-600"} />
                          <Label htmlFor="page-contact-email" className={`cursor-pointer ${isDark ? "text-gray-300" : "text-gray-700"}`}>{t("contact.emailLabel")}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="phone" id="page-contact-phone" disabled={!phoneProvided} className={isDark ? "border-white/20 text-blue-500" : "border-gray-400 text-red-600"} />
                          <Label htmlFor="page-contact-phone" className={`cursor-pointer ${phoneProvided ? "" : "opacity-50"} ${isDark ? "text-gray-300" : "text-gray-700"}`}>{t("contact.phoneLabel")}</Label>
                        </div>
                      </RadioGroup>
                    </div>
                    <BotProtection onToken={setCaptchaToken} isDark={isDark} />
                    <Button
                      type="submit"
                      disabled={submitting || !canSubmit}
                      className={`w-full text-white rounded-none py-6 font-semibold uppercase tracking-wider disabled:opacity-50 ${accentBg}`}
                    >
                      {submitting ? t("vehicleModal.sending") : t("vehicleModal.sendInquiry")}
                    </Button>
                  </form>
                </div>
              </section>

              <div className="mt-10">
                <Link
                  to="/inventory"
                  className={`inline-flex items-center gap-2 text-sm uppercase tracking-wider font-semibold ${accent}`}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("vehiclePage.backToInventory")}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
